import { FlaskConical, TestTube2, Beaker, ShieldCheck, CheckCircle2 } from "lucide-react";
import { IMAGES } from "@/lib/company";
import { Link } from "react-router-dom";

const instruments = [
  { icon: FlaskConical, name: "HPLC", desc: "High-Performance Liquid Chromatography for active marker assay." },
  { icon: TestTube2, name: "UV Spectrophotometer", desc: "Quantitative UV/Vis analysis for standardization verification." },
  { icon: Beaker, name: "TLC", desc: "Thin Layer Chromatography for phytochemical fingerprinting." },
  { icon: Beaker, name: "GC", desc: "Gas Chromatography for volatile compound profiling." },
  { icon: TestTube2, name: "Spectrometer", desc: "Elemental & spectral analysis." },
  { icon: FlaskConical, name: "pH Meter", desc: "Precision pH profiling of extracts and formulations." },
];

const testing = [
  "Active ingredient assay (HPLC / UV / Gravimetry)",
  "Heavy metals screening (Lead, Arsenic, Cadmium, Mercury)",
  "Microbial analysis (TPC, Yeast & Mold, E. coli, Salmonella, S. aureus, Pseudomonas)",
  "Pesticide residue testing",
  "Physical characterization (sieve, bulk density, organoleptic properties)",
];

export default function Quality() {
  return (
    <div data-testid="quality-page">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.lab} alt="Laboratory" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-24 w-full">
          <span className="inline-block px-4 py-1.5 bg-brand-gold text-brand-green-dark text-[11px] font-bold uppercase tracking-[0.25em] rounded-sm mb-5">Quality Assurance</span>
          <h1 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-tight max-w-4xl">
            Uncompromising <span className="text-brand-gold">Quality Control</span>
          </h1>
          <p className="mt-6 text-white/85 text-lg max-w-3xl">
            Every batch, verified. Every shipment, documented. Every partner, GMP-certified.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-brand-charcoal/85 leading-relaxed text-lg">
            At Aad Labs, quality is not an afterthought — it is our foundation. We work exclusively with manufacturers who maintain stringent Quality Control & Quality Assurance departments operating under:
          </p>
          <ul className="mt-6 grid sm:grid-cols-3 gap-4">
            {["ISO 22000:2005 Standards", "WHO-GMP Guidelines", "HACCP Protocols"].map((s) => (
              <li key={s} className="flex items-center gap-3 p-4 bg-brand-cream border border-neutral-200 rounded-md">
                <ShieldCheck className="w-5 h-5 text-brand-green" />
                <span className="font-semibold text-brand-charcoal">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Instruments */}
      <section className="py-20 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Testing Infrastructure</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline-center">Modern Analytical Instruments</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {instruments.map((i, idx) => (
              <div key={idx} data-testid={`instrument-${idx}`} className="bg-white p-7 rounded-lg border border-neutral-200 hover:border-brand-gold transition-colors">
                <div className="w-12 h-12 rounded-md bg-brand-green/10 flex items-center justify-center mb-4">
                  <i.icon className="w-6 h-6 text-brand-green" />
                </div>
                <h3 className="font-display font-bold text-brand-charcoal text-lg">{i.name}</h3>
                <p className="mt-2 text-sm text-brand-charcoal/75">{i.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testing */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Testing Scope</span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline-center">Every Batch. Every Parameter.</h2>
          </div>
          <div className="space-y-3">
            {testing.map((t) => (
              <div key={t} className="flex items-start gap-3 p-4 bg-brand-cream rounded-md border-l-4 border-brand-green">
                <CheckCircle2 className="w-5 h-5 text-brand-green shrink-0 mt-0.5" />
                <span className="text-brand-charcoal">{t}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-brand-charcoal/75 italic">
            We provide batch-specific Certificates of Analysis (CoA) with every shipment.
          </p>
        </div>
      </section>

      <section className="py-16 bg-[#212121] text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h3 className="font-display font-bold text-2xl md:text-3xl">Need a sample CoA for your quality team?</h3>
          <Link to="/contact" data-testid="quality-cta" className="mt-6 btn-gold inline-flex">Request Documentation</Link>
        </div>
      </section>
    </div>
  );
}
